import React, { useState, useEffect } from 'react';
import { getStoredSocialLinks, fetchSocialLinksFromServer } from '../utils/storage';

export default function SocialSidebar() {
  const [links, setLinks] = useState(getStoredSocialLinks());

  useEffect(() => {
    // Initial fetch from server deferred to avoid blocking critical network chains
    const timer = setTimeout(() => {
      fetchSocialLinksFromServer().then(setLinks).catch(() => {});
    }, 3000);

    // Event listener for real-time updates from Admin panel
    const handleUpdate = () => {
      setLinks(getStoredSocialLinks());
    };

    window.addEventListener('aurahair_data_change', handleUpdate);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('aurahair_data_change', handleUpdate);
    };
  }, []);

  const socialItems = [
    {
      id: 'facebook',
      name: 'Facebook',
      url: links.facebook || 'https://www.facebook.com/rotterdamaurahair',
      gradient: 'from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600',
      icon: (
        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      )
    },
    {
      id: 'instagram',
      name: 'Instagram',
      url: links.instagram || 'https://www.instagram.com/rotterdamaurahair',
      gradient: 'from-blue-500 to-indigo-600 hover:from-sky-400 hover:to-blue-600',
      icon: (
        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      )
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      url: links.tiktok || 'https://www.tiktok.com/@rotterdamaurahair',
      gradient: 'from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600',
      icon: (
        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-1.01v8.83c-.02 1.94-.58 3.89-1.71 5.48-1.22 1.74-3.09 2.94-5.18 3.32-2.12.38-4.36-.07-6.18-1.19-1.8-1.1-3.13-2.86-3.71-4.88-.58-2.02-.38-4.26.54-6.11.93-1.84 2.56-3.27 4.54-3.95.89-.31 1.83-.47 2.77-.47.16 0 .32.01.48.02v4.12c-.44-.12-.91-.18-1.37-.16-1.09.04-2.15.53-2.86 1.37-.71.84-1 1.96-.8 3.05.21 1.09.91 2.03 1.89 2.54.98.51 2.16.53 3.16.05 1-.48 1.69-1.48 1.79-2.59.04-.61.02-1.23.02-1.84V.02h2.92z" />
        </svg>
      )
    },
    {
      id: 'youtube',
      name: 'YouTube',
      url: links.youtube || 'https://www.youtube.com/@rotterdamaurahair',
      gradient: 'from-red-600 to-red-700 hover:from-red-500 hover:to-red-600',
      icon: (
        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      )
    }
  ];

  return (
    <aside 
      aria-label="Social Media Links"
      className="fixed left-2 sm:left-3 top-28 sm:top-32 z-40 flex flex-col items-center gap-1.5 sm:gap-2 p-1 sm:p-1.5 bg-slate-900/85 backdrop-blur-xl border border-blue-500/30 rounded-2xl shadow-xl shadow-blue-900/30 transition-all duration-300 hover:border-blue-400/60"
    >
      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse mb-0.5" />
      
      {socialItems.map((item) => (
        <a
          key={item.id}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          title={item.name}
          className={`group relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br ${item.gradient} text-white shadow-md shadow-blue-600/30 transition-all duration-300 hover:scale-110 hover:shadow-blue-500/50 active:scale-95`}
        >
          {item.icon}
          
          {/* Tooltip on right */}
          <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1 text-xs font-semibold text-white bg-slate-900/95 border border-blue-500/40 rounded-lg shadow-lg whitespace-nowrap opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">
            {item.name}
          </span>
        </a>
      ))}
    </aside>
  );
}
